import React, { useMemo, memo } from "react";
import { Table, ConfigProvider, Checkbox } from "antd";

/**
 * DynamicTable
 * 
 * Synchronized with dynamic-form-fe architecture.
 * Optimized for SimpeldesWebApp with Orange Theme and Localized Formatting.
 */
function DynamicTable({ fields = [], data = [], pageSize = 10, tableConfig = null, height = 400 }) {
  // --- Hooks (Must be called before any early returns) ---
  const fieldLabelMap = useMemo(() => {
    const map = {};
    (fields || []).forEach(f => { map[f.id] = f.label; });
    return map;
  }, [fields]);

  const isGrouped = !!(tableConfig?.groupBy);
  const columnGroups = tableConfig?.columnGroups || [];
  const hasGroups = columnGroups.length > 0;

  const scrollY = useMemo(() => {
    if (!height) return 180;
    // Calculate internal offset based on features
    let offset = 45; // base padding and borders
    if (hasGroups) {
      offset += 90; // taller grouped header (approx 2 rows)
    } else {
      offset += 50; // standard header
    }

    const showTotalRow = tableConfig?.showTotalRow ?? true;
    if (showTotalRow) {
      offset += 45; // summary row height
    }

    return Math.max(height - offset, 80);
  }, [height, hasGroups, tableConfig?.showTotalRow]);

  if (!fields || fields.length === 0)
    return <p style={{ color: "#999", fontSize: 12 }}>⚠️ Konfigurasi kolom tidak ditemukan</p>;

  // --- Separate Total row from regular paginated data ---
  const totalRow = data.find((r) => r._isTotalRow) || null;
  const regularData = data.filter((r) => !r._isTotalRow);

  // --- Determine visible real fields ---
  // Ensure fields in column groups are also visible to prevent them being filtered out
  const allGroupedFieldIds = new Set((tableConfig?.columnGroups || []).flatMap((g) => g.fields || []));

  const visibleFields = isGrouped
    ? fields.filter(
      (f) =>
        f.id === tableConfig.groupBy ||
        (tableConfig.sumFields || []).includes(f.id) ||
        (tableConfig.displayOnlyFields || []).includes(f.id) ||
        allGroupedFieldIds.has(f.id)
    )
    : fields;

  // --- Formatting Helpers ---
  const isCurrency = (id, label) => {
    const l = (label || fieldLabelMap[id] || "").toLowerCase();
    // Keywords that strongly suggest currency (Rp.)
    const keywords = ["pagu", "realisasi", "harga", "biaya", "dana", "nominal", "besaran", "anggaran", "budget", "belanja", "rp", "idr", "omzet"];
    return keywords.some(k => l.includes(k));
  };

  const formatVal = (val, id, label = null) => {
    if (val === undefined || val === null || val === "" || val === "-") return "-";

    // Convert to number for formatting if it's a numeric-like value
    let num = val;
    if (typeof val === "string") {
      // Use the same smart logic to check if it's a number
      const str = val.replace(/\s/g, "");
      if (str.length > 0 && !isNaN(str.replace(/\./g, "").replace(",", "."))) {
        // If we can parse it, we treat it as a number for formatting
        // But wait, it's safer to just let toLocaleString handle it if it's already a number
      }
    }

    if (typeof val !== "number") {
      // Try parsing string values for consistent formatting
      const parsed = parseFloat(String(val).replace(/\./g, "").replace(",", "."));
      if (!isNaN(parsed) && String(val).match(/^[0-9.,\s]+$/)) {
        num = parsed;
      } else {
        return val;
      }
    }

    const formatted = num.toLocaleString("id-ID");
    if (isCurrency(id, label)) return `Rp. ${formatted}`;
    return formatted;
  };

  // --- Helper: render a regular data cell ---
  const renderCell = (text, record, id) => {
    // 1. Prioritize data provided directly (from BE or state)
    if (text !== undefined && text !== null && text !== "-") {
      if (typeof text === "number" || (typeof text === "string" && text.match(/^[0-9.,\s]+$/))) {
        return formatVal(text, id);
      }
      if (typeof text === "boolean") return <Checkbox checked={text} disabled style={{ cursor: 'default' }} />;
      return text;
    }

    // 2. Fallback: Horizontal Grand Sum for real-time preview
    if (String(id).startsWith("__gs_")) {
      const groupIdx = parseInt(id.replace("__gs_", ""));
      const group = columnGroups[groupIdx];
      if (group && group.grandSumFields?.length > 0) {
        const sum = group.grandSumFields.reduce((acc, fId) => {
          const val = record.values?.[fId] ?? record[fId];
          // Use the internal parseNum logic or a simpler version here
          const n = typeof val === "number" ? val : parseFloat(String(val || "").replace(/\./g, "").replace(",", "."));
          return acc + (isNaN(n) ? 0 : n);
        }, 0);
        return (
          <span style={{ fontWeight: 700, color: "#f1582e" }}>
            {formatVal(sum, id, group.grandSumLabel)}
          </span>
        );
      }
    }

    if (typeof text === "boolean") {
      return <Checkbox checked={text} disabled style={{ cursor: 'default' }} />;
    }
    return text ?? "-";
  };

  // --- Helper: build a leaf column definition ---
  const makeCol = (id, label) => ({
    title: label,
    dataIndex: ["values", id],
    key: id,
    render: (text, record) => renderCell(text, record, id),
    align: "center",
    width: 150, // Added fixed width for better scroll experience
  });

  // --- Build AntD column tree ---
  let columns;

  if (hasGroups) {
    const allGroupedFieldIds = new Set(columnGroups.flatMap((g) => g.fields || []));

    const standaloneColumns = visibleFields
      .filter((f) => !allGroupedFieldIds.has(f.id))
      .map((f) => makeCol(f.id, f.label));

    const groupParents = columnGroups
      .map((g, i) => {
        const groupFieldIds = new Set(g.fields || []);
        const hasGrandSum = !!(g.grandSumLabel && g.grandSumFields?.length > 0);

        const children = visibleFields
          .filter((f) => groupFieldIds.has(f.id))
          .map((f) => makeCol(f.id, f.label));

        if (hasGrandSum) children.push(makeCol(`__gs_${i}`, g.grandSumLabel));

        return children.length > 0
          ? { title: g.title || `Grup ${i + 1}`, children }
          : null;
      })
      .filter(Boolean);

    columns = [...standaloneColumns, ...groupParents];
  } else {
    columns = visibleFields.map((f) => makeCol(f.id, f.label));
  }

  // --- Append Computed Columns (Formula) at Specific Index ---
  if (tableConfig?.computedColumns && Array.isArray(tableConfig.computedColumns)) {
    const sortedComputed = [...tableConfig.computedColumns].sort((a, b) => (a.orderIndex ?? 999) - (b.orderIndex ?? 999));

    sortedComputed.forEach((c) => {
      const idx = c.orderIndex ?? 999;
      const colDef = makeCol(c.title, c.title);

      if (idx >= columns.length) {
        columns.push(colDef);
      } else if (idx <= 0) {
        columns.unshift(colDef);
      } else {
        // Insert at the specified index
        columns.splice(idx, 0, colDef);
      }
    });
  }

  // --- Flatten nested column tree → leaf columns ---
  const getLeafColumns = (cols) => {
    const leaves = [];
    const walk = (list) =>
      list.forEach((c) => (c.children ? walk(c.children) : leaves.push(c)));
    walk(cols);
    return leaves;
  };

  // --- AntD Summary row (Total) ---
  const renderSummary = () => {
    const showTotalRow = tableConfig?.showTotalRow ?? true;
    if (!showTotalRow) return null;
    if (!totalRow && regularData.length === 0) return null;

    const leafCols = getLeafColumns(columns);
    if (leafCols.length === 0) return null;

    const sumFields = tableConfig?.sumFields || [];

    // --- Robust Number Parser ---
    const parseNum = (v) => {
      if (v === undefined || v === null || v === "") return 0;
      if (typeof v === "number") return v;

      let str = String(v).replace(/[Rp\s%$]/gi, "");

      // Smart detection for Indonesian vs US/Standard formats
      if (str.includes(",")) {
        // Indonesian format: 2.000.000,00 -> . is thousands, , is decimal
        str = str.replace(/\./g, "").replace(",", ".");
      } else if (str.includes(".")) {
        // Ambiguous: could be 2.000 (thousands) or 0.83 (decimal)
        if ((str.match(/\./g) || []).length > 1) {
          // Multiple dots -> definitely thousands
          str = str.replace(/\./g, "");
        } else {
          // Single dot. Check if it's likely a thousands separator (followed by exactly 3 digits)
          const parts = str.split(".");
          if (parts[1].length === 3 && parts[0].length > 0 && parts[0] !== "0") {
            // e.g. 1.000 or 1.234 -> thousands
            str = str.replace(".", "");
          } else {
            // e.g. 0.83 or 1.2 or 1.2345 -> decimal
          }
        }
      }

      const n = parseFloat(str);
      return isNaN(n) ? 0 : n;
    };

    // 1. Pre-calculate values for all columns to identify the first summed column
    const colValues = leafCols.map((col) => {
      const fieldKey = Array.isArray(col.dataIndex) ? col.dataIndex[1] : col.dataIndex;

      // Try to get existing total from totalRow
      let val = totalRow?.values?.[fieldKey] ?? totalRow?.[fieldKey];

      const isGrandSum = String(fieldKey).startsWith("__gs_");
      const shouldSum = sumFields.includes(fieldKey) || isGrandSum;

      // Force local calculation if BE value is missing/zero but we have rows to sum
      const isFalsy = val === undefined || val === null || val === 0 || val === "0" || val === "";

      if (shouldSum && isFalsy && regularData.length > 0) {
        if (isGrandSum) {
          const groupIdx = parseInt(String(fieldKey).replace("__gs_", ""));
          const group = columnGroups[groupIdx];
          if (group && group.grandSumFields) {
            val = regularData.reduce((acc, row) => {
              const rowSum = (group.grandSumFields || []).reduce((subAcc, fId) => {
                const v = row.values?.[fId] ?? row[fId];
                return subAcc + parseNum(v);
              }, 0);
              return acc + rowSum;
            }, 0);
          }
        } else {
          val = regularData.reduce((acc, row) => {
            const v = row.values?.[fieldKey] ?? row[fieldKey];
            return acc + parseNum(v);
          }, 0);
        }
      }
      return val;
    });

    // 2. Determine how many columns to merge for the "Total" label
    // Merge columns up to the first predefined sum/grand-sum column to keep them separate
    const firstSumIdx = leafCols.findIndex(col => {
      const fieldKey = Array.isArray(col.dataIndex) ? col.dataIndex[1] : col.dataIndex;
      return sumFields.includes(fieldKey) || String(fieldKey).startsWith("__gs_");
    });
    const mergeToIdx = firstSumIdx === -1 ? leafCols.length : firstSumIdx;

    return (
      <Table.Summary fixed="bottom">
        <Table.Summary.Row>
          {/* A. Merged "Total" Label Cell */}
          {mergeToIdx > 0 ? (
            <Table.Summary.Cell index={0} colSpan={mergeToIdx} align="center">
              <span style={{ fontWeight: 700, fontSize: 13 }}>TOTAL</span>
            </Table.Summary.Cell>
          ) : (
            <Table.Summary.Cell index={0} align="center">
              <span style={{ fontWeight: 700, fontSize: 13 }}>TOTAL</span>
            </Table.Summary.Cell>
          )}

          {/* B. Individual Sum Cells */}
          {leafCols.map((col, i) => {
            if (mergeToIdx > 0 && i < mergeToIdx) return null;
            if (mergeToIdx === 0 && i === 0) return null;

            const val = colValues[i];
            const isGS = String(col.key).startsWith("__gs_");
            let label = null;
            if (isGS) {
              const gIdx = parseInt(String(col.key).replace("__gs_", ""));
              label = columnGroups[gIdx]?.grandSumLabel;
            }

            return (
              <Table.Summary.Cell key={col.key || i} index={i} align="center">
                <span style={{ fontWeight: 700 }}>
                  {val !== undefined && val !== null ? formatVal(val, col.key, label) : ""}
                </span>
              </Table.Summary.Cell>
            );
          })}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: "#f1582e",
            headerColor: "#ffffff",
            headerBorderRadius: 8,
            cellPaddingInline: 16,
            cellPaddingBlock: 12,
            fontSize: 13,
          },
        },
      }}
    >
      <style>{`
        .ant-table-summary td {
          background: #fff7ed !important;
          border-top: 2.5px solid #F97316 !important;
          color: #F97316 !important;
        }
        .ant-table-summary span {
          color: #F97316 !important;
        }
        /* Make header background orange */
        .ant-table-thead > tr > th {
          background-color: #f1582e !important;
          color: #ffffff !important;
          border-bottom: 1px solid #ffffff33 !important;
        }
        /* Fix AntD 4 sticky header for grouped headers */
        .ant-table-sticky-holder {
          background-color: #f1582e !important;
        }
      `}</style>

      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Table
          columns={columns}
          dataSource={regularData}
          rowKey="_id"
          size="middle"
          summary={renderSummary}
          pagination={false}
          scroll={{ x: 'max-content', y: scrollY }}
          bordered
          style={{ flex: 1 }}
        />
      </div>
    </ConfigProvider>
  );
}

export default memo(DynamicTable);
